'use client'

import type { AnchorHTMLAttributes, ElementType, ReactNode } from 'react'
import type { LinkProps } from 'next/link'
import * as AriakitMenu from '@ariakit/react/menu'
import { Row } from '@christiankaindl/lyts'
import Button from '@shared/components/Button/Button'
import * as menuStyles from './Menu.css'
import { useMenuLayout } from './Menu'

export interface MenuItemSlots {
  /** A lucide icon: left in action lists, right in selection lists. */
  icon?: ElementType
  iconColor?: string
  /** A flag, avatar or dot in the icon column instead of `icon`. */
  decoration?: ReactNode
  /** Right-aligned secondary text, e.g. a count. */
  hint?: ReactNode
  /** A node after the hint, e.g. a shortcut or a chevron. */
  trailing?: ReactNode
}

interface MenuItemProps
  extends
    AriakitMenu.MenuItemProps,
    Pick<MenuItemSlots, 'icon' | 'iconColor'>,
    Pick<
      AnchorHTMLAttributes<HTMLAnchorElement>,
      'target' | 'rel' | 'download'
    > {
  /**
   * Render the item as a `next/link` anchor. Prefer this over passing a
   * raw `<Link>` via `render` — Button's link integration carries the
   * shared menu item styling (flex/gap/icon alignment), whereas a bare
   * `<Link>` falls back to default anchor block layout and stacks the
   * icon above the text.
   */
  href?: LinkProps['href']
  /** Destructive action: red label and icon. */
  danger?: boolean
}

function ItemIcon({
  icon: Icon,
  color,
}: {
  icon: ElementType
  color?: string
}) {
  return (
    <Icon
      size={16}
      width={16}
      style={{ color }}
      className={menuStyles.itemIcon}
      aria-hidden
    />
  )
}

/**
 * The parts of a menu row in their fixed order: control, label, hint,
 * trailing, then the icon column in selection lists.
 */
export function MenuItemContent({
  lead,
  icon: Icon,
  iconColor,
  decoration,
  hint,
  trailing,
  children,
}: MenuItemSlots & { lead?: ReactNode; children?: ReactNode }) {
  const layout = useMenuLayout()
  const iconNode =
    decoration ?? (Icon ? <ItemIcon icon={Icon} color={iconColor} /> : null)
  return (
    <>
      {layout === 'selection'
        ? (lead ?? <span className={menuStyles.checkSpacer} aria-hidden />)
        : iconNode}
      <span className={menuStyles.label}>{children}</span>
      {hint !== undefined && <span className={menuStyles.hint}>{hint}</span>}
      {trailing}
      {layout === 'selection' && iconNode && (
        <Row asChild gap='5px' xAlign='flex-end'>
          <span className={menuStyles.decoration} aria-hidden>
            {iconNode}
          </span>
        </Row>
      )}
    </>
  )
}

/**
 * An action row. `children` render as they are, so a caller may pass its own
 * icon, avatar or check before the label; `icon` is the shared icon slot,
 * which also aligns the labels of icon-less rows in the same menu.
 */
export function MenuItem({
  children,
  icon,
  iconColor,
  href,
  danger = false,
  ...props
}: MenuItemProps) {
  return (
    <AriakitMenu.MenuItem
      render={
        props.render ?? (
          <Button
            variant='flat'
            href={href}
            className={`${menuStyles.item}${danger ? ` ${menuStyles.danger}` : ''} ${props.className ?? ''}`}
          />
        )
      }
      {...props}
    >
      {icon ? <ItemIcon icon={icon} color={iconColor} /> : null}
      {children}
    </AriakitMenu.MenuItem>
  )
}
